import React, { useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { STORY_UI } from '../../solution-stories/storyUiTokens';
import type { BillingBudget } from '../../../types/billing';

export interface BillingReportFilterState {
  businessUnits: string[];
  subBus: string[];
  cloudTypes: string[];
  flaggedOnly: boolean;
}

export const EMPTY_REPORT_FILTERS: BillingReportFilterState = {
  businessUnits: [],
  subBus: [],
  cloudTypes: [],
  flaggedOnly: false,
};

function uniqueSorted(values: (string | null)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v && v.trim())))).sort();
}

function pillAutocomplete(
  label: string,
  options: string[],
  selected: string[],
  onChange: (values: string[]) => void,
) {
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      onChange={(_e, newValue) => onChange(newValue)}
      disableCloseOnSelect
      sx={{ minWidth: 200 }}
      renderTags={(value, getTagProps) =>
        value.slice(0, 1).map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            size="small"
            label={value.length > 1 ? `${option} +${value.length - 1}` : option}
            sx={{
              backgroundColor: STORY_UI.accentTint,
              color: STORY_UI.navy2,
              fontWeight: 600,
              fontSize: '11px',
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selected.length ? undefined : 'All'}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: STORY_UI.radiusPill,
              backgroundColor: STORY_UI.surface,
              fontFamily: STORY_UI.fontSans,
              fontSize: '13px',
            },
          }}
        />
      )}
    />
  );
}

export interface BillingReportFiltersProps {
  rows: BillingBudget[];
  value: BillingReportFilterState;
  onChange: (value: BillingReportFilterState) => void;
}

export function BillingReportFilters({ rows, value, onChange }: BillingReportFiltersProps): React.ReactElement {
  const businessUnitOptions = useMemo(() => uniqueSorted(rows.map((r) => r.business_unit)), [rows]);
  const subBuOptions = useMemo(() => uniqueSorted(rows.map((r) => r.sub_bu)), [rows]);
  const cloudTypeOptions = useMemo(() => uniqueSorted(rows.map((r) => r.cloud_type)), [rows]);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {pillAutocomplete('Business Unit', businessUnitOptions, value.businessUnits, (v) =>
        onChange({ ...value, businessUnits: v }),
      )}
      {pillAutocomplete('Sub-BU', subBuOptions, value.subBus, (v) => onChange({ ...value, subBus: v }))}
      {pillAutocomplete('Cloud Type', cloudTypeOptions, value.cloudTypes, (v) =>
        onChange({ ...value, cloudTypes: v }),
      )}
      <Chip
        label="Flagged only"
        clickable
        onClick={() => onChange({ ...value, flaggedOnly: !value.flaggedOnly })}
        sx={{
          borderRadius: STORY_UI.radiusPill,
          fontWeight: 500,
          fontFamily: STORY_UI.fontSans,
          backgroundColor: value.flaggedOnly ? STORY_UI.ink : STORY_UI.surface,
          color: value.flaggedOnly ? '#fff' : STORY_UI.inkSoft,
          border: `1px solid ${value.flaggedOnly ? STORY_UI.ink : STORY_UI.line}`,
        }}
      />
    </Box>
  );
}

export function applyReportFilters(rows: BillingBudget[], filters: BillingReportFilterState): BillingBudget[] {
  return rows.filter((row) => {
    if (filters.businessUnits.length && !filters.businessUnits.includes(row.business_unit || '')) return false;
    if (filters.subBus.length && !filters.subBus.includes(row.sub_bu || '')) return false;
    if (filters.cloudTypes.length && !filters.cloudTypes.includes(row.cloud_type || '')) return false;
    if (filters.flaggedOnly && !row.is_flagged) return false;
    return true;
  });
}
